import React, { HTMLAttributes, MouseEventHandler, ReactNode, useEffect, useRef, useState } from 'react';
import Box from '../../components/Box/Box';
import SecondaryIconButton from '../../components/IconButton/Secondary';
import Title5 from '../../components/Title/Title5';
import getClassNames from '../../utils/className/getClassNames';
import { Link } from '../../components/Link';
import Large from '../../components/Text/Large';

const gridClassNames = getClassNames({ parent: 'Carousel', name: 'grid' });
const prevButtonClassNames = getClassNames({ parent: 'Carousel', name: 'navButton', modifiers: 'prev' });
const nextButtonClassNames = getClassNames({ parent: 'Carousel', name: 'navButton', modifiers: 'next' });

type CarouselHeaderActionProps =
  | { text: string; href: string; onClick?: MouseEventHandler }
  | { text: string; href?: never; onClick: MouseEventHandler };
type TCarouselProps = Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'children' | 'name'> & {
  title: string;
  children: ReactNode;
  bodyText?: string;
  headerClassName?: string;
  prevButtonAriaLabel?: string;
  nextButtonAriaLabel?: string;
  action?: CarouselHeaderActionProps;
};

const Carousel = ({
  title,
  bodyText,
  headerClassName = '',
  children,
  prevButtonAriaLabel,
  nextButtonAriaLabel,
  action,
  ...rest
}: TCarouselProps) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [navigationEnabled, setNavigationEnabled] = useState(false);
  const [carouselRefWidth, setCarouselRefWidth] = useState(0);
  const [carouselOffset, setCarouselOffset] = useState(0);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [scrolledToBegin, setScrolledToBegin] = useState(false);

  // small tolerance to account for fractional pixels and subpixel rendering
  const SCROLL_EPSILON = 2;
  const hasBodytext = Boolean(bodyText);
  const hasAction = Boolean(action && action.text && (action.href || action.onClick));
  const headerClassNames = getClassNames({ parent: 'Carousel', name: 'header', custom: headerClassName });

  const isScrolledToEnd = (element: HTMLDivElement) => {
    return element ? element.offsetWidth + element.scrollLeft >= element.scrollWidth - SCROLL_EPSILON : false;
  };

  const isScrolledToBegin = (element: HTMLDivElement) => {
    return element ? element.scrollLeft <= SCROLL_EPSILON : false;
  };

  const handleNextClick = () => {
    if (carouselRef.current) {
      setCarouselOffset(carouselRef.current.scrollLeft + carouselRefWidth);
    }
  };

  const handlePreviousClick = () => {
    if (carouselRef.current) {
      setCarouselOffset(carouselRef.current.scrollLeft - carouselRefWidth);
    }
  };

  useEffect(() => {
    const carouselElement = carouselRef.current;
    const handleScroll = (event: Event) => {
      const element = event.currentTarget as HTMLDivElement;
      setScrolledToEnd(isScrolledToEnd(element));
      setScrolledToBegin(isScrolledToBegin(element));
    };

    if (carouselElement) {
      const scrolledToEndAtInit = isScrolledToEnd(carouselElement);
      setScrolledToEnd(scrolledToEndAtInit);
      setScrolledToBegin(isScrolledToBegin(carouselElement));
      setNavigationEnabled(!scrolledToEndAtInit);
      setCarouselRefWidth(carouselElement.offsetWidth);
      carouselElement.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (carouselElement) {
        carouselElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const carouselElement = carouselRef.current;

      if (carouselElement) {
        const scrolledToEndAtInit = isScrolledToEnd(carouselElement);
        setScrolledToEnd(scrolledToEndAtInit);
        setScrolledToBegin(isScrolledToBegin(carouselElement));
        setNavigationEnabled(!scrolledToEndAtInit);
        setCarouselRefWidth(carouselElement.offsetWidth);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const carouselElement = carouselRef.current;

    if (carouselElement && carouselElement.scrollLeft !== carouselOffset) {
      const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

      carouselElement.scrollTo({
        left: carouselOffset,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    }
  }, [carouselOffset]);

  return (
    <Box {...rest} as="div" name="Carousel">
      <div className={headerClassNames}>
        <div>
          <Title5>{title}</Title5>
          {hasBodytext && <Large>{bodyText}</Large>}
        </div>

        {hasAction && (
          <Link href={action?.href} onClick={action?.onClick} role={action?.href ? 'link' : 'button'}>
            {action?.text}
          </Link>
        )}
      </div>

      <div ref={carouselRef} className={gridClassNames}>
        {children}
      </div>

      {navigationEnabled && !scrolledToBegin && (
        <SecondaryIconButton
          className={prevButtonClassNames}
          ariaLabel={prevButtonAriaLabel}
          name="back"
          onClick={handlePreviousClick}
        />
      )}
      {navigationEnabled && !scrolledToEnd && (
        <SecondaryIconButton
          className={nextButtonClassNames}
          ariaLabel={nextButtonAriaLabel}
          name="arrow_tailed_right"
          onClick={handleNextClick}
        />
      )}
    </Box>
  );
};

export default Carousel;
